import React, {useState, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {FormControl} from '@material-ui/core';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import DashboardContent from 'components/DashboardContent/styles';
import DashboardHeader from 'components/DashboardHeader/styles';
import Headline from 'components/Headline/styles';
import ByocProgressBar from 'components/ByocProgressBar';
import {
  ByocOnboardingContainer,
  ByocOnboardingTitle,
  ByocOnboardingSubtitle,
  ByocOnboardingText,
  ByocOnboardingLoadingOverlay
} from 'routes/App/byocOnboarding/styles';
import {
  MenuSetupContainer,
  MenuSourceField,
  MenuSetupButton
} from 'routes/App/byocOnboarding/MenuSetup/styles';
import {ButtonOutline, ButtonPrimary} from 'components/buttons';
import DialogAction from 'components/DialogAction/styles';
import DialogActions from 'components/DialogActions/styles';
import FieldLabel from 'components/FieldLabel/styles';
import FieldInput from 'components/FieldInput';
import SelectComponent from 'components/select/SelectComponent';
import {PROGRESS_BAR_STEPS} from 'helpers/constants/byocOnboarding';
import AdjustPricingModal from 'routes/App/byocOnboarding/MenuSetup/AdjustPricingModal';
import SelectWrapper from 'components/select/SelectComponentStyles';
import {DashboardField} from 'routes/App/dashboard/styles';
import {
  UPDATE_BYOC_ONBOARDING_STEP,
  SAVE_BYOC_ADJUSTMENT_PRICE_RULES
} from 'store/byocOnboarding/actions';
import Loading from 'components/shared/Utils/Loading';
import {EmailMarketingSpinner} from 'routes/App/emailMarketing/components/styles';

const MenuSetup = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const {data, loading, globalLoading} = useSelector(
    (state) => state.byocOnboardingReducer || {}
  );
  const menuSetupData = data?.menuSetup || data?.MenuSetup || {};

  const [menuSource, setMenuSource] = useState(
    menuSetupData?.source || menuSetupData?.Source || 'Sauce'
  );
  const [adjustmentPriceRules, setAdjustmentPriceRules] = useState(
    menuSetupData?.adjustmentPriceRules ||
      menuSetupData?.AdjustmentPriceRules ||
      []
  );
  const [isAdjustPricingModalOpen, setIsAdjustPricingModalOpen] = useState(
    false
  );

  // Update state when data changes
  useEffect(() => {
    const source = menuSetupData?.source || menuSetupData?.Source;
    if (source) {
      setMenuSource(source);
    }
    const rules =
      menuSetupData?.adjustmentPriceRules ||
      menuSetupData?.AdjustmentPriceRules;
    if (rules) {
      setAdjustmentPriceRules(rules);
    }
  }, [menuSetupData]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const menuOptions = [{_id: 'Sauce', FullName: 'Sauce'}];

  return (
    <DashboardContent className='DashboardContent'>
      <DashboardHeader>
        <Headline className='mTop5'>Self Delivery</Headline>
      </DashboardHeader>
      {globalLoading ? (
        <EmailMarketingSpinner>
          <Loading />
        </EmailMarketingSpinner>
      ) : (
        <>
          <ByocProgressBar currentStep={2} steps={PROGRESS_BAR_STEPS} />
          <MenuSetupContainer>
            <ByocOnboardingTitle>Menu Setup</ByocOnboardingTitle>
            <ByocOnboardingText className='mTop10'>
              Select which menu should act as the single source of truth for all
              of your channels
            </ByocOnboardingText>

            <DashboardField className='wAutoSpace paddingL0 paddingB0 mTop20'>
              <FieldLabel className='FieldLabelRegular'>
                <span>*</span> Menu Source
              </FieldLabel>
              <SelectWrapper className='SelectMd w100'>
                <FormControl className='select-formcontrol'>
                  <Select
                    value={menuSource}
                    name='menuSource'
                    onChange={(e) => {
                      setMenuSource(e.target.value);
                    }}
                    MenuProps={{
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left'
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left'
                      },
                      getContentAnchorEl: null
                    }}
                  >
                    {menuOptions.map((option) => (
                      <MenuItem key={option._id} value={option._id}>
                        {option.FullName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </SelectWrapper>
            </DashboardField>

            <ByocOnboardingText className='mTop35'>
              Make sure your Direct menu prices are lower than on 3rd party to
              maximize direct channel profitability.
            </ByocOnboardingText>

            <MenuSetupButton>
              <ButtonPrimary
                className='ButtonMedium'
                onClick={() => setIsAdjustPricingModalOpen(true)}
                type='button'
              >
                <span>Adjust Pricing</span>
              </ButtonPrimary>
            </MenuSetupButton>

            <ByocOnboardingText className='mTop35'>
              Please check all of your menus pricing to make sure there are no
              issues before you go live
            </ByocOnboardingText>

            <MenuSetupButton>
              <ButtonPrimary
                type='button'
                className='ButtonMedium'
                onClick={() => {
                  window.open('/menu-editor/items', '_blank');
                }}
              >
                <span>Verify Menu Prices</span>
              </ButtonPrimary>
            </MenuSetupButton>

            <DialogActions className='justifyEnd mTop32'>
              <DialogAction className='w30'>
                <ButtonOutline
                  onClick={() =>
                    history.push('/byoc-onboarding/sign-up-success')
                  }
                  type='button'
                >
                  <span>Back</span>
                </ButtonOutline>
              </DialogAction>
              <DialogAction className='w30'>
                <ButtonPrimary
                  onClick={() => {
                    dispatch(
                      UPDATE_BYOC_ONBOARDING_STEP({
                        menuSetup: {
                          source: menuSource,
                          isFinished: true
                        },
                        successCallback: () => {
                          history.push(
                            '/byoc-onboarding/order-source-integration'
                          );
                        }
                      })
                    );
                  }}
                  type='button'
                  disabled={loading}
                >
                  <span>Continue</span>
                </ButtonPrimary>
              </DialogAction>
            </DialogActions>
            {loading && (
              <ByocOnboardingLoadingOverlay>
                <Loading />
              </ByocOnboardingLoadingOverlay>
            )}
          </MenuSetupContainer>
        </>
      )}

      <AdjustPricingModal
        isOpen={isAdjustPricingModalOpen}
        onClose={() => {
          if (!loading) {
            setIsAdjustPricingModalOpen(false);
          }
        }}
        onApply={(pricingData) => {
          // Convert pricing data to adjustmentPriceRules format (camelCase)
          const rules = Object.entries(pricingData)
            .filter(([_, value]) => value.amount && value.amount !== '')
            .map(([platformId, value]) => {
              // Map platform IDs from modal to API format (backend expects: uber, grubhub, doordash)
              const platformMap = {
                uber: 'uber',
                grubhub: 'grubhub',
                doordash: 'doordash',
                slice: 'slice'
              };
              return {
                platform: platformMap[platformId] || platformId,
                rateAmountDiff: parseFloat(value.amount) || 0,
                rateType: value.type || 'fixed'
              };
            });

          // Save adjustment price rules via separate API call
          dispatch(
            SAVE_BYOC_ADJUSTMENT_PRICE_RULES({
              adjustmentPriceRules: rules,
              successCallback: () => {
                setAdjustmentPriceRules(rules);
                setIsAdjustPricingModalOpen(false);
              }
            })
          );
        }}
        initialPricing={adjustmentPriceRules}
        isLoading={loading}
      />
    </DashboardContent>
  );
};

export default MenuSetup;
